/**
 * SuggestDialog — lightweight "propose a missing category/field" dialog for
 * the seller product editor (PIM Wave P3 Task 5).
 *
 * Consumes the Task 2 backend: `POST /sellers/me/suggestions` (ownership-
 * gated, 409 `pim.suggestion_limit_reached` on the 10-open cap) and
 * `GET /sellers/me/suggestions` (`{ suggestions, open_count }`, used for the
 * "you have N open suggestions" feedback and to pre-empt the cap client-side
 * — the submit-time 409 is still handled as a backstop for a stale count).
 *
 * Handles exactly two `kind`s (`"category" | "field"`) — the backend also
 * accepts `"option"`, but P3 exposes no seller entry point for it. No
 * `field_type` picker on the seller side; the admin chooses the type at
 * approval. For `kind="category"`, the currently-selected primary category
 * (if any) rides as `parent_category_id`, a placement hint only — the admin
 * can re-place it regardless.
 *
 * A single `BottomSheet` (no sibling dialogs needed — this is a one-step
 * form, not a multi-action drawer like Task 3/4's review/suggestion
 * drawers).
 */
import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BottomSheet, Button, Field } from "@garden/ui";
import { ApiError } from "@/lib/api-client";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { SELLER_DASHBOARD_NS, registerSellerDashboardI18n } from "@/features/seller-dashboard/api";
import {
  useSellerSuggestions,
  useSubmitSuggestion,
} from "@/features/seller-dashboard/hooks/useSellerSuggestions";

export interface SuggestDialogProps {
  open: boolean;
  onClose: () => void;
  kind: "category" | "field";
  /** Current primary category id, passed as a placement hint for `kind="category"`. */
  parentCategoryId?: string | null;
  /** Resolved display name of `parentCategoryId`, shown as read-only context. */
  parentCategoryLabel?: string | null;
}

const NAME_MAX = 200;
const NOTE_MAX = 1000;
const OPEN_CAP = 10;

export function SuggestDialog({
  open,
  onClose,
  kind,
  parentCategoryId,
  parentCategoryLabel,
}: SuggestDialogProps): ReactElement {
  registerSellerDashboardI18n();
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const isDesktop = useIsDesktop();

  const suggestionsQuery = useSellerSuggestions();
  const submit = useSubmitSuggestion();

  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [limitBlocked, setLimitBlocked] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setNote("");
      setLimitBlocked(false);
    }
  }, [open]);

  const openCount = suggestionsQuery.data?.open_count ?? 0;
  const capReached = openCount >= OPEN_CAP;
  const nameValid = name.trim().length >= 1 && name.trim().length <= NAME_MAX;
  const submitDisabled = !nameValid || capReached || submit.isPending;

  const handleSubmit = async (): Promise<void> => {
    if (submitDisabled) return;
    setLimitBlocked(false);
    try {
      await submit.mutateAsync({
        kind,
        name: name.trim(),
        note: note.trim() === "" ? null : note.trim(),
        parent_category_id: kind === "category" ? (parentCategoryId ?? null) : null,
      });
      toast.success(t("suggestions.toast.submitted"));
      setName("");
      setNote("");
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === "pim.suggestion_limit_reached") {
        setLimitBlocked(true);
        return;
      }
      toast.error(t("suggestions.toast.error"));
    }
  };

  const title =
    kind === "category" ? t("suggestions.category.title") : t("suggestions.field.title");
  const intro =
    kind === "category" ? t("suggestions.category.intro") : t("suggestions.field.intro");
  const showLimitWarning = capReached || limitBlocked;

  return (
    <BottomSheet open={open} onClose={onClose} title={title} desktop={isDesktop}>
      <div className="flex flex-col gap-4" data-testid="suggest-dialog-body">
        <p className="text-sm text-muted-foreground">{intro}</p>

        {kind === "category" && parentCategoryLabel ? (
          <p
            data-testid="suggest-parent-hint"
            className="rounded-[var(--g-radius)] border border-border bg-secondary px-3 py-2 text-xs text-secondary-foreground"
          >
            {t("suggestions.category.parentHint", { category: parentCategoryLabel })}
          </p>
        ) : null}

        <Field
          label={t("suggestions.name.label")}
          placeholder={t("suggestions.name.placeholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={NAME_MAX}
          hint={`${name.length}/${NAME_MAX}`}
          data-testid="suggest-name-input"
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="suggest-note" className="text-sm font-medium text-foreground">
            {t("suggestions.note.label")}
          </label>
          <textarea
            id="suggest-note"
            data-testid="suggest-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("suggestions.note.placeholder")}
            rows={3}
            maxLength={NOTE_MAX}
            className="w-full resize-none rounded-[var(--g-radius)] border border-border bg-card px-3 py-2 text-sm text-foreground outline-none"
            aria-describedby="suggest-note-hint"
          />
          <p id="suggest-note-hint" className="text-xs text-muted-foreground">
            {note.length}/{NOTE_MAX}
          </p>
        </div>

        <p className="text-xs text-muted-foreground" data-testid="suggest-open-count">
          {t("suggestions.openCount", { count: openCount })}
        </p>

        {showLimitWarning ? (
          <p
            role="alert"
            data-testid="suggest-limit-reached"
            className="rounded-[var(--g-radius)] border border-[var(--g-error)]/40 bg-[var(--g-error)]/5 px-3 py-2 text-sm text-[var(--g-error)]"
          >
            {t("suggestions.limitReached")}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={submit.isPending}
            className="min-h-[44px]"
          >
            {t("suggestions.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="suggest-submit-btn"
            loading={submit.isPending}
            disabled={submitDisabled}
            onClick={() => void handleSubmit()}
            className="min-h-[44px]"
          >
            {t("suggestions.submit")}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

export default SuggestDialog;
